import { useId } from 'react'

type LockerSvgProps = {
  open?: boolean
}

const VENTS = [0, 5, 10]
const RACKET_HOLES = [
  [-5, -6],
  [-1.7, -6],
  [1.7, -6],
  [5, -6],
  [-6.5, -3],
  [-3.2, -3],
  [0, -3],
  [3.2, -3],
  [6.5, -3],
  [-7, 0],
  [-3.5, 0],
  [0, 0],
  [3.5, 0],
  [7, 0],
  [-6.5, 3],
  [-3.2, 3],
  [0, 3],
  [3.2, 3],
  [6.5, 3],
  [-5, 6],
  [-1.7, 6],
  [1.7, 6],
  [5, 6],
]

export const LockerSvg = ({ open = false }: LockerSvgProps) => {
  const uid = useId().replace(/:/g, '')

  const renderDoor = (x: number, y: number, key: string) => (
    <g key={key}>
      <rect
        className="locker-svg-door"
        x={x}
        y={y}
        width="47"
        height="55"
        rx="2"
        fill={`url(#${uid}-locker-door)`}
      />
      <path className="locker-svg-door-highlight" d={`M${x + 2} ${y + 2}h43`} />
      <g className="locker-svg-vents">
        {VENTS.map(offset => (
          <path key={offset} d={`M${x + 15} ${y + 13 + offset}h18`} />
        ))}
      </g>
      <rect
        className="locker-svg-handle"
        x={x + 7}
        y={y + 32}
        width="3"
        height="14"
        rx="1.5"
      />
    </g>
  )

  return (
    <svg
      viewBox="0 0 220 170"
      width="100%"
      height="100%"
      className={`locker-svg ${open ? 'locker-svg-open' : ''}`}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={`${uid}-locker-body`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#59656f" />
          <stop offset="1" stopColor="#26313a" />
        </linearGradient>
        <linearGradient id={`${uid}-locker-door`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#707b84" />
          <stop offset="0.2" stopColor="#4b5761" />
          <stop offset="0.72" stopColor="#303b44" />
          <stop offset="1" stopColor="#202930" />
        </linearGradient>
        <radialGradient id={`${uid}-locker-light`} cx="50%" cy="0" r="90%">
          <stop offset="0" className="locker-svg-light-stop" />
          <stop offset="1" stopColor="transparent" />
        </radialGradient>
        <filter
          id={`${uid}-locker-shadow`}
          x="-30%"
          y="-30%"
          width="170%"
          height="190%"
        >
          <feDropShadow dx="0" dy="3" stdDeviation="3" floodOpacity="0.22" />
        </filter>
      </defs>

      <ellipse className="locker-svg-ground" cx="110" cy="153" rx="94" ry="5" />
      <g filter={`url(#${uid}-locker-shadow)`}>
        <rect
          x="19"
          y="22"
          width="159"
          height="126"
          rx="4"
          fill={`url(#${uid}-locker-body)`}
        />
        <path className="locker-svg-cabinet-highlight" d="M22 25h153" />
        {renderDoor(24, 28, 'top-left')}
        {renderDoor(73, 28, 'top-middle')}
        {renderDoor(24, 85, 'bottom-left')}
        {renderDoor(73, 85, 'bottom-middle')}

        <rect
          className="locker-svg-compartment"
          x="122"
          y="28"
          width="52"
          height="114"
          rx="2"
        />
        <rect
          x="122"
          y="28"
          width="52"
          height="114"
          rx="2"
          fill={`url(#${uid}-locker-light)`}
        />
        <path
          className="locker-svg-compartment-edge"
          d="M124 30v110M172 30v110"
        />
        <rect
          className="locker-svg-lamp"
          x="136"
          y="32"
          width="25"
          height="4"
          rx="2"
        />
        <g className="locker-svg-shelf">
          <rect x="123" y="107" width="50" height="4" rx="0.7" />
          <path d="M124 107.5h48" />
          <path className="locker-svg-shelf-shadow" d="M125 112h46" />
        </g>
        <g className="locker-svg-rackets">
          <g className="locker-svg-racket" transform="rotate(-25 147 84)">
            <g transform="translate(147 63)">
              <ellipse
                className="locker-svg-racket-head-fill"
                rx="11.5"
                ry="12.5"
              />
              <path
                className="locker-svg-racket-head-outline"
                d="M-4 11.8A11.5 12.5 0 1 1 4 11.8"
              />
              <g className="locker-svg-racket-holes">
                {RACKET_HOLES.map(([x, y]) => (
                  <circle key={`left-${x}-${y}`} cx={x} cy={y} r="0.72" />
                ))}
              </g>
            </g>
            <path
              className="locker-svg-racket-frame"
              d="M143 74.8c.7 4 2 7.5 4 10.7 2-3.2 3.3-6.7 4-10.7M147 85.5V96"
            />
            <path className="locker-svg-racket-grip" d="M147 94v14" />
          </g>
          <g className="locker-svg-racket" transform="rotate(25 149 84)">
            <g transform="translate(149 63)">
              <ellipse
                className="locker-svg-racket-head-fill"
                rx="11.5"
                ry="12.5"
              />
              <path
                className="locker-svg-racket-head-outline"
                d="M-4 11.8A11.5 12.5 0 1 1 4 11.8"
              />
              <g className="locker-svg-racket-holes">
                {RACKET_HOLES.map(([x, y]) => (
                  <circle key={`right-${x}-${y}`} cx={x} cy={y} r="0.72" />
                ))}
              </g>
            </g>
            <path
              className="locker-svg-racket-frame"
              d="M145 74.8c.7 4 2 7.5 4 10.7 2-3.2 3.3-6.7 4-10.7M149 85.5V96"
            />
            <path className="locker-svg-racket-grip" d="M149 94v14" />
          </g>
        </g>

        <g className="locker-svg-bottle">
          <rect x="164.5" y="85" width="7" height="22" rx="1.5" />
          <rect x="166.5" y="82" width="3" height="4" rx="0.7" />
          <path d="M167 92h2M167 95h2" />
          <text
            x="168"
            y="98"
            textAnchor="middle"
            transform="rotate(90 168 98)"
          >
            PADEL
          </text>
        </g>

        <g className="locker-svg-padel-bag">
          <path d="M141 123h13c2.2 0 4 1.4 4.4 3.5l1.7 7.6c.3 1.3-.7 2.4-2 2.4h-22.2c-1.3 0-2.3-1.1-2-2.4l1.7-7.6c.4-2.1 2.2-3.5 4.4-3.5Z" />
          <path
            className="locker-svg-padel-bag-panel"
            d="m139 125-1.7 9h21.4l-1.7-9Z"
          />
          <path
            className="locker-svg-padel-bag-handle"
            d="M142 124v-1.2c0-.8.7-1.5 1.5-1.5h1M154 124v-1.2c0-.8-.7-1.5-1.5-1.5h-1"
          />
          <rect
            className="locker-svg-padel-bag-grip"
            x="144"
            y="120"
            width="8"
            height="2.6"
            rx="1.3"
          />
          <path
            className="locker-svg-padel-bag-webbing"
            d="M141 125v9M155 125v9"
          />
          <path className="locker-svg-padel-bag-strap" d="m138 134.5 18-10" />
          <path className="locker-svg-padel-bag-zip" d="M140 125h16" />
          <text
            className="locker-svg-padel-bag-label"
            x="148"
            y="132.5"
            textAnchor="middle"
          >
            SPORT
          </text>
        </g>

        <g className="locker-svg-closed-door">
          <rect
            className="locker-svg-door"
            x="122"
            y="28"
            width="52"
            height="114"
            rx="2"
            fill={`url(#${uid}-locker-door)`}
          />
          <path className="locker-svg-door-highlight" d="M124 30h48" />
          <g className="locker-svg-vents">
            <path d="M139 44h19" />
            <path d="M139 49h19" />
            <path d="M139 54h19" />
            <path d="M139 116h19" />
            <path d="M139 121h19" />
            <path d="M139 126h19" />
          </g>
          <rect
            className="locker-svg-handle"
            x="129"
            y="79"
            width="3"
            height="17"
            rx="1.5"
          />
        </g>

        <g className="locker-svg-moving-door">
          <path
            className="locker-svg-open-door"
            d="m176 27 32-10v130l-32-6Z"
            fill={`url(#${uid}-locker-door)`}
          />
          <path className="locker-svg-open-door-highlight" d="m179 29 26-8" />
          <g className="locker-svg-vents locker-svg-open-door-vents">
            <path d="M186 45h12" />
            <path d="M186 50h12" />
            <path d="M186 55h12" />
            <path d="M186 117h12" />
            <path d="M186 122h12" />
            <path d="M186 127h12" />
          </g>
          <rect
            className="locker-svg-open-handle"
            x="201"
            y="77"
            width="3"
            height="21"
            rx="1.5"
          />
        </g>
        <path
          className="locker-svg-foot"
          d="M27 147h12l-2 8H26ZM160 147h12l1 8h-12Z"
        />
      </g>
    </svg>
  )
}
